import { useCallback, useEffect, useReducer } from 'react'
import { Dimensions } from 'react-native'
import { breakpoints } from '../theme'

interface Sizes {
  width: number
  height: number
}

interface Dimension {
  portrait: boolean
  landscape: boolean
  xs: boolean
  sm: boolean
  md: boolean
  lg: boolean
  xl: boolean
  screenWidth: number
  screenHeight: number
  mobileWidth: number
}

export const computeDimensions = ({ width, height }: Sizes): Dimension => ({
  portrait: width <= height,
  landscape: width > height,
  xs: true,
  sm: width >= breakpoints.sm,
  md: width >= breakpoints.md,
  lg: width >= breakpoints.lg,
  xl: width >= breakpoints.xl,
  screenWidth: width,
  screenHeight: height,
  mobileWidth: width < 300 ? width : width > 450 ? 450 : width,
})

const dimensionsReducer = (state: Dimension, sizes: Sizes) => {
  const dimensions = computeDimensions(sizes)
  const is_changed = Object.keys(dimensions).some(
    (breakpoint) =>
      state[breakpoint as keyof Dimension] !==
      dimensions[breakpoint as keyof Dimension],
  )
  return is_changed ? dimensions : state
}

/**
 * @return {typeof dimensions}
 */
export function useResponsive() {
  const [dimensions, dispatch] = useReducer(
    dimensionsReducer,
    computeDimensions(Dimensions.get('window')),
  )

  const updateDimensions = useCallback(
    () => dispatch(Dimensions.get('window')),
    [],
  )

  useEffect(() => {
    const listener = Dimensions.addEventListener('change', updateDimensions)
    return () => listener?.remove()
  }, [updateDimensions])
  return dimensions
}

const dimensions = Dimensions.get('window')
const STANDARD_WIDTH = 375
const CURRENT_WIDTH = dimensions.width
const K = CURRENT_WIDTH / STANDARD_WIDTH

export function dynamicSize(size: number) {
  return K < 1 ? K * size : size
}
